import React, { useState, useEffect, useMemo } from "react";
import AsyncSelect from "react-select/async";
import { isDemoMode, isBrowserSessionDemo } from "../utils/demoConfig";
import { getMockResponse } from "../utils/mockApi";
import { resolveDropdownLabel } from "./asyncDropdownNormal";

const API_URL = process.env.REACT_APP_API_URL_BASE;

const AsyncDropdownFeedback = ({
  value,
  onChange,
  placeholder = "Search...",
  endpoint,
  subUserEndpoint,
  parentUserOnly,
  fetchOptions = {},
  labelField,
  valueField = "id",
  isFieldValid=true,
  fieldTouched=false,
  showValidFeedback=true,
  setFieldTouched,
  revalidateField,
  fieldName,
  blacklistedLabel,
  ...props
}) => {
  const [options, setOptions] = useState([]);

  const selectValue =
    value &&
    typeof value === "object" &&
    (value.label !== undefined || value.value !== undefined)
      ? value
      : null;

  const borderColor = fieldTouched ? (isFieldValid ? "#2b9e62": "#ea2817"): "#e9ecef";

  const defaultOptionsMerged = useMemo(() => {
    if (!selectValue || selectValue.value == null) {
      return options;
    }
    const has = options.some(o => String(o.value) === String(selectValue.value));
    if (has) {
      return options;
    }
    return [selectValue, ...options];
  }, [options, selectValue]);

  const formatOptions = data => {
    const rows = data && Array.isArray(data.results) ? data.results : [];
    return rows.map(item => {
      const label = resolveDropdownLabel(item, labelField, valueField);
      return {
        value: item[valueField],
        label: item.is_blacklisted ? `${label} [${blacklistedLabel}]` : label,
        isDisabled: item.is_blacklisted || false,
      };
    });
  };

  const loadOptions = (query, callback) => {
    const sub = subUserEndpoint == null ? "" : String(subUserEndpoint);
    const relPath = `${endpoint}/${sub}?search=${encodeURIComponent(query || "")}${parentUserOnly || ""}`;

    if (isDemoMode() || isBrowserSessionDemo()) {
      const data = getMockResponse(relPath);
      const formattedData = formatOptions(data);
      callback(formattedData);
      setOptions(formattedData);
      return;
    }

    fetch(`${API_URL}${relPath}`, fetchOptions)
      .then(response => {
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        return response.json();
      })
      .then(data => {
        const formattedData = formatOptions(data);
        callback(formattedData);
        setOptions(formattedData);
      })
      .catch(error => console.error(error));
  };

  useEffect(() => {
    loadOptions('', options => setOptions(options));
  }, [subUserEndpoint]);

  return (
    <AsyncSelect
      key={subUserEndpoint}
      cacheOptions
      defaultOptions={defaultOptionsMerged}
      value={selectValue}
      loadOptions={loadOptions}
      onChange={onChange}
      onFocus={() => {
        if(showValidFeedback) {
          setFieldTouched(true)
          if(fieldName && revalidateField) {
            revalidateField(fieldName)
          }
        }
      }}
      onMouseEnter={() => {
        if(showValidFeedback) {
          setFieldTouched(true)
          if(fieldName && revalidateField) {
            revalidateField(fieldName)
          }
        }
      }}
      placeholder={placeholder}
      styles={{
        menu: styles => ({ ...styles, zIndex: 10 }),
        control: (baseStyles, state) => ({
          ...baseStyles,
          borderColor: (fieldTouched && showValidFeedback) ? borderColor : baseStyles.borderColor,
          boxShadow: (state.isFocused || state.isHovered) && showValidFeedback
            ? (isFieldValid ? "0 0 0 0.2rem rgba(45, 206, 137, 0.25)" : "0 0 0 0.2rem rgba(246, 45, 81, 0.25)") 
            : "0 0 0 1px #e9ecef",
          borderRadius: "2px",
        }),
      }}
      {...props}
    />
  );
}

export default AsyncDropdownFeedback;